/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import type { CustomCellRendererProps } from 'ag-grid-react'
import { Link } from 'react-router-dom'
import { ROUTES } from '@sportninja/common/constants/app'
import colors from '@sportninja/common/constants/appColors'
import type { SeasonGridRow } from '@sportninja/common/features/profile/stats/types'
import { getResultColor } from '@sportninja/common/features/profile/stats/utils/seriesColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'

import {
  paragraphSmall,
  paragraphXSmall,
  weightMedium,
  weightSemiBold,
} from 'src/components/css'
import { SkeletonText } from 'src/components/ts-components/SkeletonBlock'

import { TEAM_PLACEHOLDER } from '../../constants'

type SeasonLabelCellProps = CustomCellRendererProps<SeasonGridRow>

/** First column of the season table: one renderer per row kind. */
const SeasonLabelCell = ({ data }: SeasonLabelCellProps) => {
  'use memo'

  if (data === undefined) {
    return null
  }

  switch (data.kind) {
    case 'career':
      return <span css={careerStyles}>{capitalizeWords('Career Total')}</span>
    case 'loading':
      return (
        <span aria-hidden='true' css={indentStyles}>
          <SkeletonText width='60%' />
        </span>
      )
    case 'error':
      return (
        <span css={[indentStyles, errorStyles]}>
          Could not load games for this season.
        </span>
      )
    case 'game':
      return (
        <Link
          to={`${ROUTES.GAME_ROOT}/${data.gameId}`}
          title={data.label}
          aria-label={data.label}
          css={[indentStyles, gameStyles]}
        >
          {`${data.date} · ${data.matchup} · `}
          <span css={resultStyles(getResultColor(data.result.outcome, 'dark'))}>
            {data.result.label}
          </span>
        </Link>
      )
    case 'season':
      return (
        <div css={seasonStyles}>
          <span aria-hidden='true' css={caretStyles}>
            {data.isOpen ? '▾' : '▸'}
          </span>
          <img
            src={data.teamLogo ?? TEAM_PLACEHOLDER}
            alt=''
            css={logoStyles}
          />
          <div css={namesStyles}>
            {data.teamId ? (
              <Link
                to={`${ROUTES.TEAM_ROOT}/${data.teamId}`}
                css={[ellipsisStyles, teamStyles]}
              >
                {data.teamName}
              </Link>
            ) : (
              <span css={[ellipsisStyles, teamStyles]}>{data.teamName}</span>
            )}
            {data.seasonId ? (
              <Link
                to={`${ROUTES.SCHEDULE_ROOT}/${data.seasonId}`}
                css={[ellipsisStyles, seasonNameStyles]}
              >
                {data.seasonName}
              </Link>
            ) : (
              <span css={[ellipsisStyles, seasonNameStyles]}>
                {data.seasonName}
              </span>
            )}
          </div>
        </div>
      )
  }
}

// ─── Styles ──────────────────────────────────────────────────────

/**
 * Game and loading rows sit under the season's names: caret + gap + logo + gap.
 * The cell is a flex row (it centres vertically), so the label takes its width.
 */
const indentStyles = css`
  display: block;
  flex: 1;
  min-width: 0;
  padding-left: 48px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const careerStyles = css`
  ${weightSemiBold}
  color: ${colors.PRIMARY};
`

const errorStyles = css`
  ${paragraphXSmall}
  color: ${colors.ERROR_200};
`

const gameStyles = css`
  ${paragraphXSmall}
  color: ${colors.NEUTRAL_50};

  &:hover {
    color: ${colors.PRIMARY};
  }
`

/** The result keeps its outcome colour while the rest of the link hovers. */
const resultStyles = (color: string) => css`
  color: ${color};
`

const seasonStyles = css`
  display: flex;
  flex: 1;
  align-items: center;
  gap: 8px;
  height: 100%;
  min-width: 0;
`

const caretStyles = css`
  flex-shrink: 0;
  width: 8px;
  color: ${colors.NEUTRAL_50};
`

const logoStyles = css`
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border-radius: 8px;
  object-fit: contain;
`

const namesStyles = css`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`

const ellipsisStyles = css`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const teamStyles = css`
  ${paragraphSmall}
  ${weightMedium}
  color: ${colors.WHITE};

  &:hover {
    color: ${colors.PRIMARY};
  }
`

const seasonNameStyles = css`
  ${paragraphXSmall}
  color: ${colors.NEUTRAL_50};

  &:hover {
    color: ${colors.PRIMARY};
  }
`

export default SeasonLabelCell
